import {
    Box,
    CircularProgress,
    Divider,
    List,
    Popover,
    TextField,
    Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { Theme } from '@mui/material/styles';
import { parsePhoneNumber } from 'libphonenumber-js';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import useDebounce from '../../../hooks/useDebounce';
import { useSearchQuickPhone } from '../../../sic-lib/api/hooks/useContact';
import AxiosErrorAlert from '../../Shared/Alerts/AxiosErrorAlert';
import { QuickCallCreateContactForm } from './QuickCallCreateContactForm';
import { QuickCallListItem } from './QuickCallListItem';

interface ProfileBadgePopOverProps {
    anchorEl: HTMLElement | null;
    handleClose: () => void;
}

export const QuickCallPopover = ({ anchorEl, handleClose }: ProfileBadgePopOverProps) => {
    const { classes } = useStyles();
    const open = Boolean(anchorEl);
    const [txtSearch, setTxtSearch] = React.useState<string>('');
    const debTxtSearch = useDebounce(txtSearch, 250);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const { data: searchResults, isLoading, isError, error } = useSearchQuickPhone(debTxtSearch);

    //when closing the popover, reset the search text
    React.useEffect(() => {
        if (!open) setTxtSearch('');
    }, [open]);

    const isValidNumber = React.useMemo(() => {
        try {
            const parsedNumber = parsePhoneNumber(debTxtSearch, 'US');
            return parsedNumber.isPossible();
        } catch (e) {
            return false;
        }
    }, [debTxtSearch]);

    return (
        <Popover
            anchorEl={anchorEl}
            id="account-menu"
            open={open}
            onClose={handleClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            PaperProps={{
                sx: {
                    maxWidth: '400px',
                    minWidth: '300px',
                },
                ref: containerRef,
            }}
        >
            <Grid
                container
                direction="column"
                justifyContent="center"
                alignItems="center"
                spacing={2}
                p={2}
            >
                <Grid item width="100%">
                    <TextField
                        label="Search a Name or Phone Number"
                        variant="outlined"
                        value={txtSearch}
                        onChange={e => setTxtSearch(e.target.value)}
                        fullWidth
                        FormHelperTextProps={{ sx: { textAlign: 'center' } }}
                    />
                </Grid>
                {isLoading && (
                    <Grid item>
                        <CircularProgress />
                    </Grid>
                )}
                {isError && (
                    <Grid item>
                        <AxiosErrorAlert error={error} />
                    </Grid>
                )}
            </Grid>
            <Divider />
            {!isLoading && isValidNumber && !searchResults && (
                <QuickCallCreateContactForm
                    txtSearch={debTxtSearch}
                    handleClose={handleClose}
                    containerRef={containerRef}
                />
            )}
            {!isLoading && !isValidNumber && !searchResults?.length && (
                <Box p={3}>
                    <Typography variant="body2" align="center">
                        No results found
                    </Typography>
                </Box>
            )}
            {searchResults && searchResults?.length > 0 && (
                <List sx={{ width: '100%' }} disablePadding>
                    {searchResults.map((result, index) => (
                        <QuickCallListItem
                            key={index}
                            searchResult={result}
                            handleClose={handleClose}
                        />
                    ))}
                </List>
            )}
        </Popover>
    );
};

const useStyles = makeStyles()((theme: Theme) => ({
    newContactAvatar: {
        backgroundColor: theme.palette.primary.main,
    },
}));
